import { FC, memo, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { Endpoints, TFile } from "@brg/api";
import { AppTester } from 'supporting';
import { useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LinkTo } from 'shared/constants/linkTo';
import getMetaElements from './getMetaElements';

interface MetaTagsProps {
  title?: string;
  description?: string[];
  images?: TFile[];
  canonicalInfo?: {
    isCanonical: boolean;
    contextInfos: Array<{
      domain: string;
      canonicalType: number;
    }>;
  };
}

const TARGET_SIZE = 600;

const MetaTags: FC<MetaTagsProps> = ({
  title,
  description,
  images,
  canonicalInfo,
}) => {
  const { i18n } = useTranslation();
  const isProjectPage = !!useRouteMatch('/project');
  const isTagPage = !!useRouteMatch('/tag');
  const isProfile = !!useRouteMatch(LinkTo.designer);

  const img = useMemo(() => {
    if (images) {
      const sorted = images.sort((a, b) => a.width - b.width)

      const resized = sorted.find(({ width }) => width >= TARGET_SIZE)
      const original = images.find(({ size }) => size === 'original');

      return resized || original || images[0];

    }
    return null;
  }, [TARGET_SIZE, images]);

  const titlePostfix = () => {
    if (AppTester.isHseSpace())  {
      return `HSE SPACE`;
    }
    if (AppTester.isLPUDesign()) {
      return 'Lovely Professional University';
    }
    if (AppTester.isCreativeHSE()) {
      return 'Институт развития креативных индустрий';
    }
    if (AppTester.isBrnd()) {
      return 'BRND';
    }
    if (AppTester.isDvaDesign()) {
      return 'DVA Design';
    }
    if (AppTester.isSuperPuper()) {
      return 'Super Puper Games';
    }
    if (AppTester.isAcademy()) {
      return 'Creative city';
    }
    if (AppTester.isGeoDafes()) {
      return 'Dafes';
    }
    if (AppTester.isGeograffee()) {
      return 'Geograffee';
    }
    if (AppTester.isMediiia()) {
      return 'Mediiia';
    }
    if (AppTester.isHSEDesign()) {
      return `HSE Design`;
    }
    if (AppTester.isAgeytomesh()) {
      return 'Agey Tomesh';
    }
    if (AppTester.isGalllleryArt()) {
      return 'Gallllery art';
    }
    if (AppTester.isOnlines()) {
      return 'ONLINES DESIGN SCHOOL';
    }
    if (AppTester.isSchool()) {
      return 'HSE Design';
    }
    if (AppTester.isContinuumSite()) {
      return 'Continuum';
    }
    if (AppTester.isMonaLisaTravels()) {
      return 'Mona Lisa Travels';
    }
    return 'Deziiign';
  }

  const clearMetaTags = () => {
    const tags = document.querySelectorAll(`[data-custom-tag="true"]`)
    tags.forEach((el) => {
      el.remove();
    })
  }

  useEffect(() => {
    clearMetaTags()
    const imageUrl = img && `${Endpoints.files}/${img?.url}`;

    const elements = getMetaElements({title, imageUrl, description})

    elements.forEach((el: any) => {
      const meta = document.createElement('meta')
      Object.entries(el).forEach(([key, value]) => {
        meta.setAttribute(key, value as string);
      });
      meta.setAttribute('data-custom-tag', 'true');
      document.head.prepend(meta);
    })
    return clearMetaTags
  }, [title, description, img])

  const divider = useMemo(() => {
    if (AppTester.isLPUDesign() || AppTester.isCreativeHSE()) {
      return '|'
    }
    return i18n.language === 'ru' ? 'на' : 'on';
  }, [i18n.language])

  const computeTitle = useMemo(() => {
    if ((isProjectPage || isProfile) && !!title?.length) {
      return `${title} ${divider} ${titlePostfix()}`
    }
    if (isTagPage &&!!title?.length) {
      return `${title} ${'::'} ${titlePostfix()}`
    }
    return title;
  }, [title, i18n.language, titlePostfix, isTagPage, isProjectPage, divider])

  // Логика для canonical URL
  const canonicalUrl = useMemo(() => {
    if (!canonicalInfo || !canonicalInfo.contextInfos?.length) {
      return null;
    }

    const currentDomain = window.location.hostname;
    const canonicalDomain = canonicalInfo.contextInfos.find(ctx => ctx.domain === currentDomain);
    
    if (canonicalDomain) {
      // Если текущий домен каноничный - показываем canonical
      return window.location.href;
    } else {
      // Если текущий домен не каноничный - показываем alternate с первым каноничным доменом
      const firstCanonicalDomain = canonicalInfo.contextInfos[0];
      if (firstCanonicalDomain) {
        // Создаем URL без порта для production
        const protocol = window.location.protocol;
        const path = window.location.pathname + window.location.search;
        const alternateUrl = `${protocol}//${firstCanonicalDomain.domain}${path}`;
        return alternateUrl;
      }
    }
    
    return null;
  }, [canonicalInfo]);

  const isCanonical = useMemo(() => {
    if (!canonicalInfo || !canonicalInfo.contextInfos?.length) {
      return false;
    }
    const currentDomain = window.location.hostname;
    return canonicalInfo.contextInfos.some(ctx => ctx.domain === currentDomain);
  }, [canonicalInfo]);

  useEffect(() => {
    if (computeTitle) {
      document.title = computeTitle;
    }
  }, [computeTitle]);
  
  return (
    <Helmet>
      <title>
        {computeTitle}
      </title>
      {canonicalUrl && (
        isCanonical ? (
          <link rel="canonical" href={canonicalUrl} />
        ) : (
          <link rel="alternate" href={canonicalUrl} />
        )
      )}
    </Helmet>
  );
}

export default memo(MetaTags);
import { FC, memo, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { Endpoints, TFile } from "@brg/api";
import { AppTester } from 'supporting';
import { useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LinkTo } from 'shared/constants/linkTo';
import getMetaElements from './getMetaElements';

interface MetaTagsProps {
  title?: string;
  description?: string[];
  images?: TFile[];
  canonicalInfo?: {
    isCanonical: boolean;
    contextInfos: Array<{
      domain: string;
      canonicalType: number;
    }>;
  };
}

const TARGET_SIZE = 600;

const MetaTags: FC<MetaTagsProps> = ({
  title,
  description,
  images,
  canonicalInfo,
}) => {
  const { i18n } = useTranslation();
  const isProjectPage = !!useRouteMatch('/project');
  const isTagPage = !!useRouteMatch('/tag');
  const isProfile = !!useRouteMatch(LinkTo.designer);

  const img = useMemo(() => {
    if (images) {
      const sorted = images.sort((a, b) => a.width - b.width)

      const resized = sorted.find(({ width }) => width >= TARGET_SIZE)
      const original = images.find(({ size }) => size === 'original');

      return resized || original || images[0];

    }
    return null;
  }, [TARGET_SIZE, images]);

  const titlePostfix = () => {
    if (AppTester.isHseSpace())  {
      return `HSE SPACE`;
    }
    if (AppTester.isLPUDesign()) {
      return 'Lovely Professional University';
    }
    if (AppTester.isCreativeHSE()) {
      return 'Институт развития креативных индустрий';
    }
    if (AppTester.isBrnd()) {
      return 'BRND';
    }
    if (AppTester.isDvaDesign()) {
      return 'DVA Design';
    }
    if (AppTester.isSuperPuper()) {
      return 'Super Puper Games';
    }
    if (AppTester.isAcademy()) {
      return 'Creative city';
    }
    if (AppTester.isGeoDafes()) {
      return 'Dafes';
    }
    if (AppTester.isGeograffee()) {
      return 'Geograffee';
    }
    if (AppTester.isMediiia()) {
      return 'Mediiia';
    }
    if (AppTester.isHSEDesign()) {
      return `HSE Design`;
    }
    if (AppTester.isAgeytomesh()) {
      return 'Agey Tomesh';
    }
    if (AppTester.isGalllleryArt()) {
      return 'Gallllery art';
    }
    if (AppTester.isOnlines()) {
      return 'ONLINES DESIGN SCHOOL';
    }
    if (AppTester.isSchool()) {
      return 'HSE Design';
    }
    if (AppTester.isContinuumSite()) {
      return 'Continuum';
    }
    if (AppTester.isMonaLisaTravels()) {
      return 'Mona Lisa Travels';
    }
    return 'Deziiign';
  }

  const clearMetaTags = () => {
    const tags = document.querySelectorAll(`[data-custom-tag="true"]`)
    tags.forEach((el) => {
      el.remove();
    })
  }

  useEffect(() => {
    clearMetaTags()
    const imageUrl = img && `${Endpoints.files}/${img?.url}`;

    const elements = getMetaElements({title, imageUrl, description})

    elements.forEach((el: any) => {
      const meta = document.createElement('meta')
      Object.entries(el).forEach(([key, value]) => {
        meta.setAttribute(key, value as string);
      });
      meta.setAttribute('data-custom-tag', 'true');
      document.head.prepend(meta);
    })
    return clearMetaTags
  }, [title, description, img])

  const divider = useMemo(() => {
    if (AppTester.isLPUDesign() || AppTester.isCreativeHSE()) {
      return '|'
    }
    return i18n.language === 'ru' ? 'на' : 'on';
  }, [i18n.language])

  const computeTitle = useMemo(() => {
    if ((isProjectPage || isProfile) && !!title?.length) {
      return `${title} ${divider} ${titlePostfix()}`
    }
    if (isTagPage &&!!title?.length) {
      return `${title} ${'::'} ${titlePostfix()}`
    }
    return title;
  }, [title, i18n.language, titlePostfix, isTagPage, isProjectPage, divider])

  // Логика для canonical URL
  const canonicalUrl = useMemo(() => {
    if (!canonicalInfo || !canonicalInfo.contextInfos?.length) {
      return null;
    }

    const currentDomain = window.location.hostname;
    const canonicalDomain = canonicalInfo.contextInfos.find(ctx => ctx.domain === currentDomain);
    
    if (canonicalDomain) {
      // Если текущий домен каноничный - показываем canonical
      return window.location.href;
    } else {
      // Если текущий домен не каноничный - показываем alternate с первым каноничным доменом
      const firstCanonicalDomain = canonicalInfo.contextInfos[0];
      if (firstCanonicalDomain) {
        // Создаем URL без порта для production
        const protocol = window.location.protocol;
        const path = window.location.pathname + window.location.search;
        const alternateUrl = `${protocol}//${firstCanonicalDomain.domain}${path}`;
        return alternateUrl;
      }
    }
    
    return null;
  }, [canonicalInfo]);

  const isCanonical = useMemo(() => {
    if (!canonicalInfo || !canonicalInfo.contextInfos?.length) {
      return false;
    }
    const currentDomain = window.location.hostname;
    return canonicalInfo.contextInfos.some(ctx => ctx.domain === currentDomain);
  }, [canonicalInfo]);

  useEffect(() => {
    if (computeTitle) {
      document.title = computeTitle;
    }
  }, [computeTitle]);
  
  return (
    <Helmet>
      <title>
        {computeTitle}
      </title>
      {canonicalUrl && (
        isCanonical ? (
          <link rel="canonical" href={canonicalUrl} />
        ) : (
          <link rel="alternate" href={canonicalUrl} />
        )
      )}
    </Helmet>
  );
}

export default memo(MetaTags);
